/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * <rk-overlay state="hidden|connecting|offline" message="...">
 *
 * Centered overlay for stream-pending and stream-missing states.
 */

type State = "hidden" | "connecting" | "offline";

@customElement("rk-overlay")
export class RkOverlay extends LitElement {
  static styles = css`
    :host {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      pointer-events: none;
      opacity: 0;
      transition: opacity var(--rk-dur-slow) var(--rk-ease-out);
      z-index: var(--rk-z-overlay);
    }
    :host([state="connecting"]),
    :host([state="offline"]) {
      opacity: 1;
      pointer-events: auto;
    }
    .panel {
      display: grid;
      gap: 10px;
      place-items: center;
      padding: 22px 28px;
      background: var(--rk-bg-glass-strong);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid var(--rk-border);
      border-radius: var(--rk-r-lg);
      box-shadow: var(--rk-shadow-lg);
      min-width: 240px;
      text-align: center;
    }
    .spinner {
      width: 36px;
      height: 36px;
      border-radius: 999px;
      border: 2px solid rgba(255, 255, 255, 0.08);
      border-top-color: var(--rk-brand-primary);
      animation: spin 0.9s linear infinite;
    }
    :host([state="offline"]) .spinner { display: none; }
    .title {
      font-weight: 700;
      font-size: var(--rk-text-md);
      color: var(--rk-text);
    }
    .sub {
      font-size: var(--rk-text-sm);
      color: var(--rk-text-muted);
      max-width: 260px;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;

  @property({ reflect: true }) state: State = "hidden";
  @property() message = "";

  render() {
    const title =
      this.state === "offline"
        ? "No live stream right now"
        : "Connecting to live stream…";
    const sub =
      this.state === "offline"
        ? "We'll connect automatically as soon as the stream starts."
        : this.message || "Buffering live feed";
    return html`
      <div class="panel">
        <div class="spinner"></div>
        <div class="title">${title}</div>
        <div class="sub">${sub}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rk-overlay": RkOverlay;
  }
}
